import { memo } from "react";
import { Mail, Phone, Github, Linkedin, MapPin } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

/**
 * Single contact row — renders as <a> if href is set, <div> otherwise.
 */
const ContactRow = ({ item }) => {
  const inner = (
    <>
      <div className="contact-icon">{item.icon}</div>
      <div style={{ textAlign: "left", overflow: "hidden", flex: 1 }}>
        <div
          style={{
            fontSize: 10,
            color: "var(--text-muted)",
            fontFamily: "'DM Mono',monospace",
            letterSpacing: 1,
          }}
        >
          {item.label}
        </div>
        <div
          style={{
            fontWeight: 500,
            fontSize: "0.87rem",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {item.value}
        </div>
      </div>
      {item.href && (
        <span
          style={{
            color: "var(--text-muted)",
            flexShrink: 0,
            fontSize: "0.9rem",
          }}
        >
          →
        </span>
      )}
    </>
  );

  return item.href ? (
    <a
      href={item.href}
      target={item.href.startsWith("http") ? "_blank" : undefined}
      rel="noreferrer"
      className="contact-link"
    >
      {inner}
    </a>
  ) : (
    <div className="contact-link" style={{ cursor: "default" }}>
      {inner}
    </div>
  );
};

/**
 * Contact section — centred layout with a stacked list of contact methods.
 * Labels come from the active locale (t.contact.labels).
 */
const ContactSection = memo(() => {
  const { t } = useLanguage();
  const c = t.contact;
  const lbl = c.labels;

  const CONTACTS = [
    {
      label: lbl.email,
      value: "adilibraheemkoyava@gmail.com",
      href: "mailto:adilibraheemkoyava@gmail.com",
      icon: <Mail size={17} style={{ color: "var(--accent)" }} />,
    },
    {
      label: lbl.phone,
      value: "+49 15510 091074",
      href: "tel:+4915510091074",
      icon: <Phone size={17} style={{ color: "var(--accent)" }} />,
    },
    {
      label: lbl.github,
      value: "github.com/koyava",
      href: "https://github.com/koyava",
      icon: <Github size={17} style={{ color: "var(--accent)" }} />,
    },
    {
      label: lbl.linkedin,
      value: "linkedin.com/in/adil-ibraheem-koyava",
      href: "https://www.linkedin.com/in/adil-ibraheem-koyava/",
      icon: <Linkedin size={17} style={{ color: "var(--accent)" }} />,
    },
    {
      label: lbl.location,
      value: "Hamburg, Germany",
      href: null,
      icon: <MapPin size={17} style={{ color: "var(--accent)" }} />,
    },
  ];

  return (
    <section id="contact" className="section">
      <div className="section-overlay" />
      <div className="container">
        <div className="divider" />

        <div style={{ maxWidth: 660, margin: "0 auto", textAlign: "center" }}>
          <p className="section-tag fade-up" style={{ textAlign: "center" }}>
            {c.tag}
          </p>
          <h2
            className="section-title fade-up fade-up-delay-1"
            style={{ textAlign: "center" }}
          >
            {c.titleLine1}
            <br />
            {c.titleLine2}
          </h2>
          <p
            style={{
              color: "var(--text-muted)",
              lineHeight: 1.82,
              marginBottom: 42,
              fontSize: "0.95rem",
            }}
            className="fade-up fade-up-delay-2"
          >
            {c.description}
          </p>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 11,
              maxWidth: 450,
              margin: "0 auto",
            }}
            className="fade-up fade-up-delay-3"
          >
            {CONTACTS.map((item) => (
              <ContactRow key={item.label} item={item} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
});

export default ContactSection;
